import React from "react";
import { Calendar, Repeat, Compass, Heart } from "lucide-react";
import MarketingPage, { MarketingSection, Pillar } from "@/apps/rider/components/MarketingPage";

export default function About() {
  return (
    <MarketingPage
      eyebrow="About Link"
      title="For Getting There — And Back."
      lede="Link is a scheduled commute platform built for the trip you take three to five days a week. We don't run a rideshare app; we run a transportation service that shows up — same time, same route, every day."
      back="/"
      backLabel="Home"
      testId="about-page"
      cta={{ to: "/", label: "Schedule your route" }}
    >
      <MarketingSection eyebrow="What we are" title="A scheduled commute platform." testId="about-what">
        <p>
          Link replaces the daily ritual of opening a rideshare app, comparing fares, waiting for an acceptance, and hoping the driver shows up. We replace it with one decision — your route — and a single subscription that runs that route on autopilot.
        </p>
        <p>
          Built around recurring round-trip commutes with a verified driver pool that stabilizes around your schedule, Link is for people who'd rather think about anything other than how they're getting to work tomorrow morning.
        </p>
      </MarketingSection>

      {/* Operating principles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Pillar Icon={Calendar} title="Predictable" testId="about-pillar-predictable">
          One route, same schedule, locked-in pricing. The opposite of "let me check the app."
        </Pillar>
        <Pillar Icon={Repeat} title="Round-trip native" testId="about-pillar-round-trip">
          Pickup and return are the same booking, the same plan, the same driver pool.
        </Pillar>
        <Pillar Icon={Compass} title="Local first" testId="about-pillar-local">
          We launch one metro at a time. Real driver pools, real coverage, real trust.
        </Pillar>
        <Pillar Icon={Heart} title={'Operations, not "tech"'} testId="about-pillar-ops">
          Showing up reliably is harder — and more valuable — than a shinier app. We optimize for showing up.
        </Pillar>
      </div>

      <MarketingSection eyebrow="Why this matters" title="A commute should be the calmest part of your day." testId="about-thesis">
        <p>
          The five hours a week you spend commuting shouldn't be a nightly puzzle. It shouldn't be a fare gamble. It shouldn't be a string of unknown drivers handed off through an algorithm.
        </p>
        <p>
          It should be the same trip, the same window, the same handful of trusted drivers — running cleanly in the background of your life. That's what we're building.
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="Where we are" title="Kansas City today, more soon." testId="about-coverage">
        <p>
          Link operates across the greater Kansas City metro. We grow city-by-city, only after we've onboarded enough drivers to keep pickup windows tight in a new market. <a href="/service-areas" className="text-[#ed6500] underline-offset-4 hover:underline">See current service areas →</a>
        </p>
      </MarketingSection>

      <MarketingSection eyebrow="Get in touch" title="Real humans, real inboxes." testId="about-contact">
        <p>
          Press, partnerships, drivers, riders, neighborhoods asking us to come next — all the same inbox.
        </p>
        <p>
          <a className="text-[#ed6500] underline-offset-4 hover:underline" href="mailto:lamar@lefcoholdings.com">lamar@lefcoholdings.com</a>
          {" · "}
          <a className="text-[#ed6500] underline-offset-4 hover:underline" href="/support/contact">Send a message →</a>
        </p>
      </MarketingSection>
    </MarketingPage>
  );
}
